/**
 * Minimal toast strip — Stage I uses this to surface layer-permission
 * rejections from the SceneClient so a player who tried to draw on the
 * DM layer (via a stale UI state or a shortcut mishap) sees a clear
 * "you can't write there" instead of a silent no-op.
 *
 * Kept as a module-level store so any code path can fire a toast
 * without plumbing a React context through the whole page. Toasts
 * auto-dismiss after a short timer; the component just reads the
 * current queue via `useSyncExternalStore`.
 */

import { useSyncExternalStore } from "react";
import "./Toasts.css";

const AUTO_DISMISS_MS = 5000;

export type ToastKind = "info" | "warn" | "error";

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

type Listener = () => void;

class ToastStore {
  private toasts: Toast[] = [];
  private listeners = new Set<Listener>();
  private nextId = 1;

  get(): Toast[] {
    return this.toasts;
  }

  push(kind: ToastKind, message: string): void {
    const id = this.nextId++;
    this.toasts = [...this.toasts, { id, kind, message }];
    this.notify();
    setTimeout(() => this.dismiss(id), AUTO_DISMISS_MS);
  }

  dismiss(id: number): void {
    const next = this.toasts.filter((t) => t.id !== id);
    if (next.length === this.toasts.length) return;
    this.toasts = next;
    this.notify();
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  private notify(): void {
    for (const l of [...this.listeners]) l();
  }
}

export const toastStore = new ToastStore();

export default function Toasts() {
  const toasts = useSyncExternalStore(
    (cb) => toastStore.subscribe(cb),
    () => toastStore.get(),
    () => toastStore.get(),
  );

  if (toasts.length === 0) return null;

  return (
    <div className="toasts" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={`toasts__item toasts__item--${t.kind}`}>
          <span className="toasts__message">{t.message}</span>
          <button
            type="button"
            className="toasts__close"
            onClick={() => toastStore.dismiss(t.id)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
